/**
 * The loaded lattice, held OUTSIDE React.
 *
 * A pack carries megabytes of typed arrays. React 19 in development serializes
 * props on every commit, so putting these in state or passing them as props
 * would be a per-frame catastrophe. Same rule as the swarm bays: big buffers
 * live in a module store, components get a subscription and small summaries.
 */

import { decodePack, peekManifest, deriveKey, toJson, toFloat32, type PackManifest } from "../pure/packCodec";
import type { QuantizedVectors } from "../pure/quant";
import type { EggDef } from "../pure/canary";

export type FactRecord = {
  /** Row id in the source lattice. Stable across rebuilds; the array index is not. */
  id: number;
  fact: string;
  category: string | null;
  quote: string | null;
  ref: string | null;
  status: string | null;
  resonance: number | null;
};

export type EntityGroup = { name: string; rows: number[] };
export type Relation = { row: number; s: string; p: string; o: string; c: number | null };

export type LoadedPack = {
  manifest: PackManifest;
  facts: FactRecord[];
  vectors: QuantizedVectors;
  entities: EntityGroup[];
  relations: Relation[];
  eggs: EggDef[];
  bytes: number;
};

let current: LoadedPack | null = null;
const listeners = new Set<(p: LoadedPack | null) => void>();

export const getPack = (): LoadedPack | null => current;

export function subscribePack(fn: (p: LoadedPack | null) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function publish(p: LoadedPack | null) {
  current = p;
  for (const fn of listeners) fn(p);
}

export type LoadPackOpts = {
  passphrase?: string;
  onProgress?: (loaded: number, total: number) => void;
};

export async function loadPack(url: string, opts: LoadPackOpts = {}): Promise<LoadedPack> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`pack fetch failed: ${res.status} ${res.statusText}`);

  const total = Number(res.headers.get("content-length") ?? 0);
  let bytes: Uint8Array;

  if (res.body && total > 0 && opts.onProgress) {
    const reader = res.body.getReader();
    const chunks: Uint8Array[] = [];
    let loaded = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      loaded += value.length;
      opts.onProgress(loaded, total);
    }
    bytes = new Uint8Array(loaded);
    let o = 0;
    for (const c of chunks) {
      bytes.set(c, o);
      o += c.length;
    }
  } else {
    bytes = new Uint8Array(await res.arrayBuffer());
  }

  // Peek first so a bad pack fails with a useful message rather than a
  // decryption error that hides the real problem.
  const { encrypted } = peekManifest(bytes);
  const key = encrypted ? await deriveKey(opts.passphrase ?? "", peekManifest(bytes).manifest.id) : undefined;

  const { manifest, sections } = await decodePack(bytes, key);

  const facts = toJson<FactRecord[]>(sections.facts);
  const scales = toFloat32(sections.scales);
  const raw = sections.vectors;
  const data = new Int8Array(raw.buffer, raw.byteOffset, raw.byteLength);

  const expected = facts.length * manifest.dim;
  if (data.length !== expected) {
    throw new Error(`pack is inconsistent: ${data.length} vector bytes for ${facts.length} facts at dim ${manifest.dim}`);
  }
  if (scales.length !== facts.length) {
    throw new Error(`pack is inconsistent: ${scales.length} scales for ${facts.length} facts`);
  }

  const pack: LoadedPack = {
    manifest,
    facts,
    vectors: { dim: manifest.dim, count: facts.length, data, scales },
    entities: sections.entities ? toJson<EntityGroup[]>(sections.entities) : [],
    relations: sections.relations ? toJson<Relation[]>(sections.relations) : [],
    eggs: sections.eggs ? toJson<EggDef[]>(sections.eggs) : [],
    bytes: bytes.length,
  };

  publish(pack);
  return pack;
}

export function unloadPack() {
  publish(null);
}
